import { clamp, HintAnimation } from '../hint-motion';
import {
  abs,
  C,
  click,
  cursorTemplate,
  DIVIDER_H,
  glass,
  icon,
  mountTemplate,
  MOTION,
  nextUid,
  orbiTemplate,
  PRESSED,
  Refs,
  renderCamera,
  renderCursor,
  ORBI_FACE_SECONDS,
  renderOrbi,
  stage,
} from './scene-kit';

/**
 * Portal-Hinweis 4 „Mehr Credits im Free-Trial erhalten" — Port des
 * Claude-Design-Films `Credits Trial Loop.dc.html` (Projekt „Portal-Hinweis 4",
 * Datei `hinweis-4-credits-trial-loop.jsx`). Schrift „14 Tage kostenlos den
 * Pro Plan nutzen" → Avatar oben rechts → Produktplan → „2 Wochen
 * Testzeitraum starten". Bühne 640×354, 8,0 s Loop; T = Abspielzeit / 1,6.
 */

const STRETCH = 1.6;
const AUTHORED = 5.0;

// Szenenanfänge in Autorenzeit (Summe der `nat`-Längen aus OM_SCENES).
const CUES = { Start: 0, Portal: 1.0, Menue: 1.5, Plaene: 2.2, Aktiv: 3.4, Loop: 4.2 };

// Geometrie
const AV = { x: 582, y: 14, size: 30 };
const BELL = { x: 546, y: 14, size: 30 };
const MENU = { x: 476, y: 52, w: 150, row: 28, pad: 6 };
const MENU_ITEMS: [string, string][] = [['card', 'Produktplan'], ['users', 'Organisation'], ['gear', 'Einstellungen'], ['logout', 'Abmelden']];
const MENU_Y = (i: number) => MENU.pad + i * MENU.row + (i === 3 ? 6 : 0);
const GX = 10, GW = 620;
const TILE_Y = 14, TILE_H = 186;
const PLANS = [
  { name: 'Free', price: '0 €', per: '', x: 14, w: 120, btn: 'active' },
  { name: 'Basic', price: '99 €', per: '/ Monat', x: 142, w: 120, btn: 'Auswählen' },
  { name: 'Professional', price: '249 €', per: '/ Monat', x: 270, w: 208, btn: 'trial' },
  { name: 'Enterprise', price: 'Individuell', per: '', x: 486, w: 120, btn: 'Anfragen' },
];
const PRO = PLANS[2];
const BTN_Y = 140, BTN_H = 32;
const PLAN_GY = 104; // Glas-Oberkante auf der Seite Produktplan
const BTN_WORLD = [GX + PRO.x + PRO.w / 2, PLAN_GY + TILE_Y + BTN_Y + BTN_H / 2];
/** Credits im Professional-Plan (von Marcel bestätigt, 15.10.2026). */
const CREDITS_TARGET = 2500;

const PILL_BASE = 'border-radius:999px;display:flex;align-items:center;justify-content:center;gap:6px;white-space:nowrap;font-size:11.5px;font-weight:600;';

function activePill(ref: string, opacity: number): string {
  return (
    `<div data-r="${ref}" style="position:absolute;inset:0;opacity:${opacity};">` +
    `<div style="position:absolute;inset:0;border-radius:999px;${PRESSED}"></div>` +
    `<div style="position:absolute;inset:0;${PILL_BASE}color:${C.text};">${icon('check', 14, C.text, 2.2)}Aktiv</div></div>`
  );
}

function secondaryPill(label: string, opacity: number, ref = ''): string {
  return (
    `<div ${ref ? `data-r="${ref}"` : ''} style="position:absolute;inset:0;opacity:${opacity};${PILL_BASE}font-weight:500;color:${C.text};` +
    `background:rgb(255 255 255 / .62);box-shadow:inset 0 1px 1px rgb(255 255 255 / .9), 0 0 0 1px rgb(83 109 145 / .18), 0 3px 8px -5px rgb(38 65 112 / .3);">${label}</div>`
  );
}

function template(uid: string): string {
  const words = (list: string[], ref: string) =>
    list.map((t) => `<span data-r="${ref}" style="display:inline-block;opacity:0;">${t}</span>`).join('');
  const intro =
    `<div data-r="intro" style="${abs(0, 108, 640, 130, 'display:flex;flex-direction:column;align-items:center;gap:6px;')}">` +
    `<div style="display:flex;align-items:center;gap:10px;">` +
    `<div style="display:flex;gap:9px;font-size:36px;font-weight:600;letter-spacing:-0.025em;color:${C.text};white-space:nowrap;">${words(['14', 'Tage', 'kostenlos'], 'w1')}</div>` +
    orbiTemplate('intro', 36, 'opacity:0;') +
    `</div>` +
    `<div style="display:flex;gap:7px;font-size:24px;font-weight:600;letter-spacing:-0.02em;color:${C.action};white-space:nowrap;margin-right:46px;">${words(['den', 'Pro', 'Plan', 'nutzen'], 'w2')}</div>` +
    `</div>`;

  const round = 'border-radius:999px;display:flex;align-items:center;justify-content:center;';
  const header =
    `<div style="${abs(22, 14, 160, 30, `display:flex;align-items:center;font-size:18px;font-weight:700;letter-spacing:-0.035em;color:${C.text};`)}">alphalearn</div>` +
    `<div style="${abs(BELL.x, BELL.y, BELL.size, BELL.size, `${round}background:rgb(255 255 255 / .5);box-shadow:inset 0 1px 1px rgb(255 255 255 / .9), 0 3px 8px -5px rgb(38 65 112 / .35);`)}">${icon('bell', 16)}</div>` +
    `<div data-r="avatar" style="${abs(AV.x, AV.y, AV.size, AV.size, `${round}font-size:11px;font-weight:600;color:${C.text};letter-spacing:0.02em;background:radial-gradient(circle at 30% 25%, rgb(255 255 255 / .9), rgb(255 255 255 / .55) 45%, rgb(180 127 255 / .35));`)}">MP</div>`;

  const title = `<div data-r="title" style="${abs(22, 64, 300, 28, `opacity:0;font-size:20px;font-weight:600;letter-spacing:-0.02em;color:${C.text};white-space:nowrap;`)}">Produktplan</div>`;

  const tiles = PLANS.map((p) => {
    let buttons: string;
    if (p.btn === 'active') {
      buttons = activePill('freeActive', 1) + secondaryPill('Auswählen', 0, 'freeSelect');
    } else if (p.btn === 'trial') {
      buttons =
        `<div data-r="trialBtn" style="position:absolute;inset:0;${PILL_BASE}color:#fff;background:${C.action};box-shadow:0 6px 14px -8px rgb(37 99 235 / .8), inset 0 1px 0 rgb(255 255 255 / .25);">2 Wochen Testzeitraum starten</div>` +
        activePill('proActive', 0);
    } else {
      buttons = secondaryPill(p.btn, 1);
    }
    const pro =
      p === PRO
        ? `<div data-r="credits" style="${abs(14, 100, 140, 26, 'display:flex;align-items:center;gap:7px;opacity:0;white-space:nowrap;')}">` +
          `<span style="width:7px;height:7px;border-radius:999px;background:${C.ok};"></span>` +
          `<span style="font-size:11px;font-weight:500;color:${C.muted};">Credits</span>` +
          `<span data-r="creditsNum" style="font-size:15px;font-weight:600;color:${C.text};font-variant-numeric:tabular-nums;">0</span></div>` +
          `<div data-r="tileOrbiBox" style="${abs(p.w - 46, 10, 34, 34, 'opacity:0;')}">${orbiTemplate('tile', 32)}</div>`
        : '';
    return (
      `<div data-r="tile" style="${abs(p.x, TILE_Y, p.w, TILE_H, 'opacity:0;')}">` +
      `<div data-r="tileFace" style="position:absolute;inset:0;border-radius:18px;background:linear-gradient(150deg, rgb(255 255 255 / .34), rgb(255 255 255 / .12));box-shadow:inset 0 1px 1px rgb(255 255 255 / .85), inset 0 -1px 1px rgb(52 76 117 / .10);"></div>` +
      `<div style="${abs(14, 16, p.w - 28, 18, `font-size:13px;font-weight:600;color:${C.text};white-space:nowrap;`)}">${p.name}</div>` +
      `<div style="${abs(14, 44, p.w - 20, 30, 'display:flex;align-items:baseline;gap:5px;white-space:nowrap;')}">` +
      `<span style="font-size:${p.price.length > 6 ? 18 : 24}px;font-weight:600;letter-spacing:-0.02em;color:${C.text};">${p.price}</span>` +
      (p.per ? `<span style="font-size:11px;font-weight:400;color:${C.muted};">${p.per}</span>` : '') +
      `</div>` +
      pro +
      `<div style="${abs(10, BTN_Y, p.w - 20, BTN_H)}">${buttons}</div>` +
      `</div>`
    );
  }).join('');

  const plans = glass(30, 0.42, `left:${GX}px;top:56px;width:${GW}px;height:262px;`, tiles, 'data-r="plans"');

  const menuH = MENU.pad * 2 + MENU.row * 4 + 6;
  const menu =
    `<div data-r="menu" style="${abs(MENU.x, MENU.y, MENU.w, menuH, 'display:none;transform-origin:80% 0;')}">` +
    glass(
      18,
      0.95,
      'inset:0;',
      MENU_ITEMS.map(
        ([ic, label], i) =>
          `<div style="${abs(MENU.pad, MENU_Y(i), MENU.w - MENU.pad * 2, MENU.row - 2, 'display:flex;')}">` +
          (i === 0 ? `<div data-r="menuSel" style="position:absolute;inset:0;border-radius:999px;opacity:0;${PRESSED}"></div>` : '') +
          `<div ${i === 0 ? 'data-r="menuFirst"' : ''} style="position:relative;flex:1;display:flex;align-items:center;gap:8px;padding:0 10px;font-size:12px;font-weight:500;color:${C.text};white-space:nowrap;">${icon(ic, 14)}${label}</div></div>`,
      ).join('') +
        `<div style="${abs(MENU.pad + 8, MENU_Y(3) - 4, MENU.w - MENU.pad * 2 - 16, 2, `background:${DIVIDER_H};`)}"></div>`,
    ) +
    `</div>`;

  const portal =
    `<div data-r="portal" style="position:absolute;inset:0;opacity:0;transform-origin:320px 176px;">` +
    header +
    title +
    plans +
    menu +
    `</div>`;

  const cursor = `<div data-r="cursorWrap" style="position:absolute;inset:0;opacity:0;">${cursorTemplate(uid)}</div>`;

  return stage(portal + cursor, intro);
}

export class CreditsTrialAnimation implements HintAnimation {
  readonly width = 640;
  readonly height = 354;
  readonly playbackSeconds = AUTHORED * STRETCH;
  /** Schlussbild: Professional „Aktiv", Credits gezählt, Orbi lacht (T = 4,1). */
  readonly stillAt = 4.1 * STRETCH;

  private refs: Refs = {};

  mount(host: HTMLElement): void {
    this.refs = mountTemplate(host, template(nextUid('al-hint-trial')));
  }

  render(t: number): void {
    this.frame(clamp(t / STRETCH, 0, AUTHORED));
  }

  private one(key: string): HTMLElement {
    return this.refs[key][0];
  }

  /** Ein Bild zur Autorenzeit T — Werte wie `Piece` in hinweis-4-credits-trial-loop.jsx. */
  private frame(T: number): void {
    const { Start: st, Portal: po, Menue: me, Plaene: pl, Aktiv: ak, Loop: lp } = CUES;
    const live = 1 - MOTION.glide(lp, lp + 0.6, T);

    const c1 = me + 0.05, c2 = me + 0.48, c3 = pl + 1.1;
    const ks = [c1, c2, c3].map((at) => click(at, T));
    const press = Math.max(...ks.map((k) => k.press));
    const ring = ks.map((k) => k.ring).find((r) => r > 0 && r < 1) || 0;

    // 1 · Kinetische Schrift
    const out = MOTION.glide(po, po + 0.3, T);
    const intro = this.one('intro');
    intro.style.opacity = String(1 - out);
    intro.style.transform = `translateY(${-30 * out}px)`;
    const word = (el: HTMLElement, s: number) => {
      const e = MOTION.enter(s, s + 0.3, T);
      el.style.opacity = String(e);
      el.style.transform = `translateY(${(1 - e) * 10}px)`;
    };
    this.refs['w1'].forEach((el, i) => word(el, 0.06 + 0.1 * i));
    this.refs['w2'].forEach((el, i) => word(el, 0.4 + 0.07 * i));
    const orbiIn = MOTION.enter(st + 0.3, st + 0.5, T);
    const introWrap = this.one('introWrap');
    introWrap.style.opacity = String(orbiIn);
    introWrap.style.transform = `scale(${0.6 + 0.4 * orbiIn})`;
    renderOrbi(
      this.refs,
      'intro',
      MOTION.glide(st + 0.36, st + 0.36 + ORBI_FACE_SECONDS * 0.85, T),
      Math.sin(Math.PI * clamp((T - (st + 0.3)) / 0.35, 0, 1)),
    );

    // 2–5 · Portal
    const portalO = MOTION.enter(po + 0.08, po + 0.4, T) * live;
    const portalS = 1.03 - 0.03 * MOTION.enter(po + 0.08, po + 0.45, T);
    const portal = this.one('portal');
    portal.style.opacity = String(portalO);
    portal.style.transform = `scale(${portalS})`;
    this.one('cursorWrap').style.opacity = String(MOTION.enter(po + 0.1, po + 0.25, T) * live);

    const open = MOTION.enter(c1 + 0.02, c1 + 0.18, T) * (1 - MOTION.enter(c2 + 0.1, c2 + 0.24, T));
    const pick = MOTION.enter(c2 - 0.02, c2 + 0.08, T);
    this.one('avatar').style.boxShadow =
      `inset 0 1px 1px rgb(255 255 255 / .95), 0 3px 8px -5px rgb(38 65 112 / .35), 0 0 0 ${2 * open}px rgb(59 130 246 / ${0.45 * open})`;

    // Kontextmenü
    const menu = this.one('menu');
    if (open <= 0.001) {
      menu.style.display = 'none';
    } else {
      menu.style.display = 'block';
      menu.style.opacity = String(open);
      menu.style.transform = `translateY(${(1 - open) * -6}px) scale(${0.97 + 0.03 * open})`;
      this.one('menuSel').style.opacity = String(pick);
      this.one('menuFirst').style.fontWeight = pick > 0.5 ? '600' : '500';
    }

    // Seite Produktplan
    const page = MOTION.glide(c2 + 0.12, c2 + 0.42, T);
    const plans = this.one('plans');
    plans.style.top = `${56 + (PLAN_GY - 56) * page}px`;
    plans.style.height = `${262 - (PLAN_GY - 56) * page}px`;
    const titleO = MOTION.enter(pl, pl + 0.24, T);
    const title = this.one('title');
    title.style.opacity = String(titleO);
    title.style.transform = `translateY(${(1 - titleO) * 6}px)`;
    this.refs['tile'].forEach((el, i) => {
      const o = MOTION.enter(pl + 0.06 + 0.07 * i, pl + 0.36 + 0.07 * i, T);
      el.style.opacity = String(o);
      el.style.transform = `translateY(${(1 - o) * 10}px) scale(${0.96 + 0.04 * o})`;
    });

    // Testzeitraum starten → Aktiv
    const act = MOTION.enter(c3 + 0.02, c3 + 0.16, T);
    this.one('freeActive').style.opacity = String(1 - act);
    this.one('freeSelect').style.opacity = String(act);
    this.one('trialBtn').style.opacity = String(1 - act);
    this.one('proActive').style.opacity = String(act);
    const glow = Math.sin(Math.PI * clamp((T - (ak + 0.02)) / 0.5, 0, 1));
    this.refs['tileFace'][PLANS.indexOf(PRO)].style.boxShadow =
      `inset 0 1px 1px rgb(255 255 255 / .85), inset 0 -1px 1px rgb(52 76 117 / .10), ` +
      `0 0 0 ${2 * glow}px rgb(59 130 246 / ${0.55 * glow}), 0 0 ${20 * glow}px rgb(59 130 246 / ${0.45 * glow})`;
    const credO = MOTION.enter(ak + 0.08, ak + 0.24, T);
    const credits = this.one('credits');
    credits.style.opacity = String(credO);
    credits.style.transform = `translateY(${(1 - credO) * 4}px)`;
    const count = Math.round(CREDITS_TARGET * MOTION.enter(ak + 0.12, ak + 0.62, T)).toLocaleString('de-DE');
    const num = this.one('creditsNum');
    if (num.textContent !== count) num.textContent = count;
    this.one('tileOrbiBox').style.opacity = String(MOTION.enter(ak + 0.04, ak + 0.18, T));
    renderOrbi(
      this.refs,
      'tile',
      MOTION.glide(ak + 0.16, ak + 0.16 + ORBI_FACE_SECONDS, T),
      Math.sin(Math.PI * clamp((T - (ak + 0.04)) / 0.35, 0, 1)),
    );

    // Zeiger
    const REST = [598, 318];
    const A = [AV.x + 16, AV.y + 17];
    const PP = [MENU.x + 62, MENU.y + MENU_Y(0) + 13];
    const ct = [0, po + 0.1, c1 - 0.06, c1 + 0.08, c2 - 0.08, c2 + 0.06, c3 - 0.12, c3 + 0.12, ak + 0.6, 99];
    const pts = [REST, REST, A, A, PP, PP, BTN_WORLD, BTN_WORLD, REST, REST];
    const cx = MOTION.path(ct, pts.map((p) => p[0]), T);
    const cy = MOTION.path(ct, pts.map((p) => p[1]), T);
    renderCursor(this.one('ring'), this.one('arrow'), cx, cy, press, ring);

    // Kamera
    const ck = [0, po + 0.4, c1 + 0.05, c2 + 0.1, pl + 0.25, pl + 0.35, pl + 0.75, lp, lp + 0.6, 99];
    const s = MOTION.path(ck, [1, 1, 1.05, 1.05, 1, 1, 1.32, 1.32, 1, 1], T);
    const fx = MOTION.path(ck, [320, 320, 400, 400, 320, 320, 384, 384, 320, 320], T);
    const fy = MOTION.path(ck, [176, 176, 140, 140, 176, 176, 214, 214, 176, 176], T);
    renderCamera(this.one('camera'), fx, fy, s);
  }
}
